import { useContext } from 'react';
import { useMutation } from 'react-query';
import { useStripe } from '@stripe/react-stripe-js';

import { ApplicationEventNames } from '~/constants/ApplicationEvents';
import { mutations } from '~/constants/mutationKeys';
import { DepositContext } from '~/context/Deposit/DepositContext';
import { SentryHelper } from '~/helpers/Sentry/SentryHelper';
import { VinovestNetworkingHelper } from '~/helpers/VinovestNetworkingHelper';
import { DepositFrequency } from '~/models/deposits/DepositFrequency';
import { depositAmountInCents, getDepositAmtWithFeesInCents } from '~/utils/deposits/depositAmountUtils';

interface TransferReturn {
    error: unknown;
    finalizeDeposit: (paymentMethodType: {
        type: string;
        id: string;
        country: string;
        startDate: string;
    }) => Promise<boolean | Error>;
    loading: boolean;
}

/** Final submit for credit card or bank transfer */
export const useConfirmDeposit = (): TransferReturn => {
    const stripe = useStripe();

    const { depositAmount, depositFrequency, destinationPortfolioId } = useContext(DepositContext);

    const finalizeDeposit = async (paymentMethod: { type: string; id: string; country: string; startDate: string }) => {
        const depositAmountCents = getDepositAmtWithFeesInCents(
            depositAmountInCents(depositAmount),
            paymentMethod.type,
            paymentMethod.country
        );
        const { managed } = VinovestNetworkingHelper.getClient();

        if (depositFrequency !== DepositFrequency.once || paymentMethod.type === 'ach') {
            try {
                if (destinationPortfolioId) {
                    const createSubscription = await managed.createPaymentSubscription(
                        destinationPortfolioId,
                        paymentMethod.id,
                        depositAmountCents,
                        depositFrequency,
                        paymentMethod.startDate
                    );

                    if (createSubscription?.status !== 204) {
                        throw new Error('Unable to complete transfer, pleases try again.');
                    }
                    return true;
                }
                return false;
            } catch (subscriptionError: any) {
                SentryHelper.captureException(subscriptionError, { extra: { paymentMethod } });
                throw new Error(subscriptionError?.toString());
            }
        } else {
            try {
                const { data: paymentIntentClientSecret } = await managed.createStripePaymentIntent(
                    depositAmountCents,
                    paymentMethod.type,
                    paymentMethod.id,
                    destinationPortfolioId
                );
                if (paymentIntentClientSecret) {
                    const confirmedPaymentIntent = await stripe?.confirmCardPayment(paymentIntentClientSecret?.secret, {
                        payment_method: paymentMethod.id
                    });
                    if (confirmedPaymentIntent?.paymentIntent?.status === 'succeeded') {
                        return true;
                    }
                    return false;
                }
                return false;
            } catch (cardError: any) {
                SentryHelper.captureException(cardError, { extra: { paymentMethod } });
                throw new Error(cardError?.toString());
            }
        }
    };

    const mutation = useMutation(
        [mutations.ConfirmDeposit, depositAmount, depositFrequency, destinationPortfolioId],
        finalizeDeposit,
        {
            onSuccess: () => {
                typeof window !== 'undefined' &&
                    window.dispatchEvent(new CustomEvent(ApplicationEventNames.DepositSuccess));
            }
        }
    );

    return { error: mutation.error, finalizeDeposit: mutation.mutateAsync, loading: mutation.isLoading };
};
